import { useSettings } from '@restaurant/api-client/react';
import { normalizeWaId, type CustomerDto, type ImportCustomersResultDto, type MarketingStatus } from '@restaurant/shared';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Search, Upload, UserPlus, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { EmptyState, PageHeader, Skeleton, Spinner, Switch, Tabs, TabsList, TabsTrigger } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { formatPhone } from '@/lib/utils';

const PAGE = 50;
const MAX_IMPORT = 2000;
// Header only: sample rows would get imported as real numbers if left in. The BOM makes Excel keep Sinhala/Tamil names intact.
const CSV_TEMPLATE = `data:text/csv;charset=utf-8,${encodeURIComponent('﻿Name,Phone\n')}`;

const FILTERS: { value: MarketingStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'opted_in', label: 'Receive promotions' },
  { value: 'unknown', label: 'Not asked' },
  { value: 'invited', label: 'Invited' },
  { value: 'opted_out', label: 'Opted out' },
];

const STATUS: Record<MarketingStatus, { label: string; variant: 'muted' | 'outline' | 'success' | 'destructive' | 'default' }> = {
  opted_in: { label: 'Receives promotions', variant: 'success' },
  unknown: { label: 'Not asked', variant: 'muted' },
  invited: { label: 'Invited', variant: 'default' },
  opted_out: { label: 'Opted out', variant: 'destructive' },
};

/** Owner-only: everyone the restaurant knows, and who agreed to promotions. Numbers can be added without a chat. */
export function CustomersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [marketing, setMarketing] = useState<MarketingStatus | 'all'>('all');
  const [dialog, setDialog] = useState<'add' | 'import' | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const list = useInfiniteQuery({
    queryKey: ['customers-admin', debounced, marketing],
    queryFn: ({ pageParam }) => api.customers.search({ search: debounced || undefined, marketing: marketing === 'all' ? undefined : marketing, limit: PAGE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => (last.length === PAGE ? pages.length * PAGE : undefined),
  });
  const rows = list.data?.pages.flat() ?? [];
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['customers-admin'] }), qc.invalidateQueries({ queryKey: ['campaign-reach'] })]);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Customers"
        description="Add numbers and record who agreed to receive promotions."
        actions={
          <>
            <Button variant="outline" onClick={() => setDialog('import')}><Upload /> Import</Button>
            <Button onClick={() => setDialog('add')}><UserPlus /> Add customer</Button>
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
        <div className="mx-auto max-w-5xl space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative sm:max-w-xs sm:flex-1">
              <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
              <Input className="pl-8" placeholder="Search name or number" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Tabs value={marketing} onValueChange={(v) => setMarketing(v as MarketingStatus | 'all')}>
              <TabsList className="flex-wrap">
                {FILTERS.map((f) => <TabsTrigger key={f.value} value={f.value}>{f.label}</TabsTrigger>)}
              </TabsList>
            </Tabs>
          </div>

          {list.isLoading ? (
            <Skeleton className="h-40" />
          ) : !rows.length ? (
            <EmptyState
              icon={<Users />}
              title={debounced || marketing !== 'all' ? 'No matches' : 'No customers yet'}
              description={debounced || marketing !== 'all' ? undefined : 'Customers appear here when they message you. You can also add numbers or import a list.'}
            />
          ) : (
            <Card className="divide-y p-0">
              {rows.map((c) => <CustomerRow key={c.id} customer={c} onChanged={refresh} />)}
            </Card>
          )}
          {list.hasNextPage ? (
            <div className="flex justify-center">
              <Button variant="outline" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>{list.isFetchingNextPage ? <Spinner /> : null} Load more</Button>
            </div>
          ) : null}
        </div>
      </div>
      <AddCustomerDialog open={dialog === 'add'} onClose={() => setDialog(null)} onDone={refresh} />
      <ImportDialog open={dialog === 'import'} onClose={() => setDialog(null)} onDone={refresh} />
    </div>
  );
}

function CustomerRow({ customer: c, onChanged }: { customer: CustomerDto; onChanged: () => Promise<unknown> }) {
  const [busy, setBusy] = useState(false);
  const [confirmOptIn, setConfirmOptIn] = useState(false);
  const status = STATUS[c.marketingStatus];
  const toggle = async (optIn: boolean) => {
    setBusy(true);
    try {
      await api.customers.setMarketing(c.id, optIn);
      toast.success(optIn ? 'Customer will receive promotions' : 'Customer unsubscribed from promotions');
      setConfirmOptIn(false);
      await onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{c.name ?? formatPhone(c.waId)}</p>
          <p className="text-xs text-muted-foreground">
            {c.name ? `${formatPhone(c.waId)} · ` : ''}
            {c.orderCount} order{c.orderCount === 1 ? '' : 's'}
          </p>
        </div>
        <Badge variant={status.variant} className="hidden sm:inline-flex">{status.label}</Badge>
        <Switch
          checked={c.marketingStatus === 'opted_in'}
          disabled={busy}
          onCheckedChange={(v) => (v && c.marketingStatus === 'opted_out' ? setConfirmOptIn(true) : void toggle(v))}
          aria-label="Receives promotions"
        />
      </div>
      {confirmOptIn ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-xs">
          <span className="flex-1">They opted out (replied STOP or asked you). Only turn this on if they asked to receive promotions again.</span>
          <Button size="sm" variant="ghost" onClick={() => setConfirmOptIn(false)}>Cancel</Button>
          <Button size="sm" disabled={busy} onClick={() => void toggle(true)}>{busy ? <Spinner /> : null} Turn on</Button>
        </div>
      ) : null}
    </div>
  );
}

function AddCustomerDialog({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => Promise<unknown> }) {
  const [f, setF] = useState({ phone: '', name: '', agreed: false });
  const [busy, setBusy] = useState(false);
  const close = () => {
    setF({ phone: '', name: '', agreed: false });
    onClose();
  };
  const submit = async () => {
    setBusy(true);
    try {
      const c = await api.customers.create({ phone: f.phone, name: f.name.trim(), defaultAddress: null });
      if (f.agreed) await api.customers.setMarketing(c.id, true);
      toast.success(f.agreed ? `${c.name ?? formatPhone(c.waId)} will receive promotions` : `${c.name ?? formatPhone(c.waId)} added`);
      await onDone();
      close();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : close())}>
      <DialogContent title="Add customer" description="An existing number is kept as it is (only the promotions choice is applied).">
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field label="Mobile number" hint="Local (077 123 4567) or international (+94 77 123 4567).">
            <Input autoFocus required value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} inputMode="tel" />
          </Field>
          <Field label="Name">
            <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={100} />
          </Field>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={f.agreed} onChange={(e) => setF({ ...f, agreed: e.target.checked })} className="mt-0.5 size-4 accent-primary" />
            They agreed to receive promotions on WhatsApp
          </label>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={close}>Cancel</Button>
            <Button type="submit" disabled={busy || !f.phone.trim() || !f.name.trim()}>{busy ? <Spinner /> : null} Add</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ParsedRow {
  line: number;
  raw: string;
  phone: string;
  name: string | null;
  waId: string | null;
  duplicate: boolean;
}

/** One customer per line: "phone", "name, phone" or "phone, name" (comma, semicolon or tab). A header line is skipped. */
export function parseCustomerList(text: string): ParsedRow[] {
  const out: ParsedRow[] = [];
  const seen = new Set<string>();
  text.split(/\r?\n/).forEach((raw, i) => {
    if (!raw.trim()) return;
    const cells = raw
      .split(/[,;\t]/)
      .map((c) => c.replace(/^\uFEFF/, '').trim().replace(/^["']|["']$/g, '').trim())
      // Excel drops the leading 0 of local mobiles (0771234567 → 771234567).
      .map((c) => (/^7\d{8}$/.test(c) ? `0${c}` : c))
      .filter(Boolean);
    const phoneIdx = cells.findIndex((c) => normalizeWaId(c) !== null);
    if (phoneIdx < 0 && !out.length && /phone|number|mobile/i.test(raw)) return;
    const waId = phoneIdx >= 0 ? normalizeWaId(cells[phoneIdx]!) : null;
    const name = cells.find((c, j) => j !== phoneIdx && /\p{L}/u.test(c)) ?? null;
    const duplicate = !!waId && seen.has(waId);
    if (waId) seen.add(waId);
    out.push({ line: i + 1, raw: raw.trim(), phone: phoneIdx >= 0 ? cells[phoneIdx]! : raw.trim(), name, waId, duplicate });
  });
  return out;
}

function ImportDialog({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => Promise<unknown> }) {
  const { data: settings } = useSettings();
  const [text, setText] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportCustomersResultDto | null>(null);
  const rows = useMemo(() => parseCustomerList(text), [text]);
  const valid = rows.filter((r) => r.waId && !r.duplicate);
  const invalid = rows.filter((r) => !r.waId);
  const duplicates = rows.filter((r) => r.duplicate);
  const tooMany = rows.length > MAX_IMPORT;

  const close = () => {
    setText('');
    setAgreed(false);
    setResult(null);
    onClose();
  };
  const readFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      setText(await file.text());
    } catch (err) {
      toastError(err);
    }
  };
  const submit = async () => {
    setBusy(true);
    try {
      // Every line goes to the server (bad ones too) so its report lines up with the file.
      const r = await api.customers.import({ rows: rows.map((row) => ({ phone: row.phone.slice(0, 30), name: row.name?.slice(0, 100) ?? null })), consentConfirmed: true });
      setResult(r);
      await onDone();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : close())}>
      <DialogContent title="Import customers" description="Paste a list or pick a CSV file. One customer per line: number, or name and number.">
        {result ? (
          <div className="grid gap-3 text-sm">
            <p>
              <b className="tabular-nums">{result.optedIn}</b> added to promotions, <b className="tabular-nums">{result.alreadyOptedIn}</b> already on the list,{' '}
              <b className="tabular-nums">{result.created}</b> new customer{result.created === 1 ? '' : 's'}.
            </p>
            {result.skippedOptedOut.length ? (
              <div>
                <p className="font-medium">Replied STOP, not changed ({result.skippedOptedOut.length})</p>
                <p className="text-xs text-muted-foreground">{result.skippedOptedOut.map((s) => (s.name ? `${s.name} (${s.phone})` : s.phone)).join(', ')}</p>
              </div>
            ) : null}
            {result.invalid.length ? (
              <div>
                <p className="font-medium">Skipped lines ({result.invalid.length})</p>
                <ul className="max-h-40 overflow-y-auto text-xs text-muted-foreground">
                  {result.invalid.map((r) => <li key={r.line}>Line {rows[r.line - 1]?.line ?? r.line}: {r.phone} ({r.reason === 'duplicate' ? 'duplicate' : 'not a mobile number'})</li>)}
                </ul>
              </div>
            ) : null}
            <div className="flex justify-end"><Button onClick={close}>Done</Button></div>
          </div>
        ) : (
          <div className="grid gap-3">
            <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder={'Nimal Perera, 0771234567\n0719876543\n+94 76 555 1234, Ayesha'} className="font-mono text-xs" />
            <Input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => void readFile(e.target.files?.[0])} />
            <a href={CSV_TEMPLATE} download="customers-template.csv" className="flex items-center gap-1 justify-self-start text-xs underline">
              <Download className="size-3" /> Download CSV template
            </a>
            {rows.length ? (
              <div className="grid gap-1 text-xs">
                <p>
                  <b className="tabular-nums">{valid.length}</b> valid
                  {duplicates.length ? <>, <b className="tabular-nums">{duplicates.length}</b> duplicate</> : null}
                  {invalid.length ? <>, <b className="tabular-nums text-destructive">{invalid.length}</b> not a mobile number</> : null}
                </p>
                {tooMany ? <p className="text-destructive">At most {MAX_IMPORT} lines per import. Split the list.</p> : null}
                <ul className="max-h-32 overflow-y-auto rounded-md border px-2 py-1 text-muted-foreground">
                  {valid.slice(0, 10).map((r) => <li key={r.line}>{r.name ? `${r.name} · ` : ''}{formatPhone(r.waId!)}</li>)}
                  {valid.length > 10 ? <li>and {valid.length - 10} more</li> : null}
                </ul>
                {invalid.length ? <p className="text-destructive">Line {invalid.slice(0, 5).map((r) => `${r.line} (${r.raw})`).join(', ')}{invalid.length > 5 ? '…' : ''}</p> : null}
              </div>
            ) : null}
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 size-4 accent-primary" />
              <span>
                These customers agreed to receive promotions from {settings?.restaurantName ?? 'us'} on WhatsApp.
                <span className="block text-xs text-muted-foreground">Messaging people who did not agree gets your number reported and limited by Meta. Anyone who replied STOP is skipped.</span>
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={close}>Cancel</Button>
              <Button disabled={busy || !agreed || !valid.length || tooMany} onClick={() => void submit()}>{busy ? <Spinner /> : null} Import {valid.length || ''}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
